<script setup>
import { computed, onBeforeUnmount, onMounted, watch } from "vue";
import { useAuthStore } from "@/stores/AuthStore";
import LoginView from "@/views/TheLoginView.vue";
import LayoutDefault from "@/views/Layout/LayoutDefault.vue";
import UndoToast from "@/components/UndoToast.vue";
import apiClient from "@/apiClient.js";
import router from "@/router/index.js";
import { getPresenceTabId } from "@/utils/presenceSession";

const auth = useAuthStore();
const isPublicRoute = computed(
  () => router.currentRoute.value.meta?.public === true,
);
const PRESENCE_PING_INTERVAL_MS = 1000 * 60;
const PRESENCE_TITLE_SUFFIX = /\s*-\s*Neptune Beta$/;
const presenceTabId = getPresenceTabId();

let presenceIntervalId = null;
let presenceSyncInFlight = false;
let lastInteractionAt = Date.now();

/* Set the page title */
router.beforeEach((to, from, next) => {
  const canShowRouteTitle = to.meta?.public === true || auth.isLoggedIn;
  const title = canShowRouteTitle ? to.meta.title : "Login";
  document.title = (title ?? "Dashboard") + " - Neptune Beta";
  next();
});

/* Get CSRF Cookie */
function refreshToken() {
  return apiClient.get("/sanctum/csrf-cookie").catch(() => {});
}

const markInteraction = () => {
  lastInteractionAt = Date.now();
};

const currentPresenceTitle = () => {
  const routeTitle = router.currentRoute.value?.meta?.title;
  if (typeof routeTitle === "string" && routeTitle.trim() !== "") {
    return routeTitle.trim();
  }

  return document.title.replace(PRESENCE_TITLE_SUFFIX, "").trim();
};

const presencePayload = () => ({
  tab_id: presenceTabId,
  path: router.currentRoute.value?.fullPath || window.location.pathname,
  title: currentPresenceTitle() || null,
  visible: document.visibilityState === "visible",
  last_interaction_at: new Date(lastInteractionAt).toISOString(),
});

const handlePingError = async (err) => {
  if (err.response?.status === 401) {
    auth.clearSession();
  }
};

const syncPresence = async ({ refreshCsrf = false } = {}) => {
  if (!auth.isLoggedIn || isPublicRoute.value || presenceSyncInFlight) {
    return;
  }

  presenceSyncInFlight = true;
  try {
    if (refreshCsrf) {
      await refreshToken();
    }

    await apiClient.post("/api/ping", presencePayload());
    auth.renewTimeoutCookie();
  } catch (err) {
    await handlePingError(err);
  } finally {
    presenceSyncInFlight = false;
  }
};

const stopPresenceTimer = () => {
  if (presenceIntervalId !== null) {
    window.clearInterval(presenceIntervalId);
    presenceIntervalId = null;
  }
};

const startPresenceTimer = () => {
  stopPresenceTimer();
  if (!auth.isLoggedIn || isPublicRoute.value) {
    return;
  }

  presenceIntervalId = window.setInterval(() => {
    void syncPresence({ refreshCsrf: true });
  }, PRESENCE_PING_INTERVAL_MS);
};

const handleVisibilityChange = () => {
  if (document.visibilityState === "visible") {
    markInteraction();
  }

  if (auth.isLoggedIn) {
    void syncPresence();
  }
};

onMounted(() => {
  markInteraction();
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("focus", markInteraction);
  window.addEventListener("keydown", markInteraction);
  window.addEventListener("pointerdown", markInteraction);
  window.addEventListener("mousemove", markInteraction);

  if (auth.isLoggedIn) {
    void syncPresence();
    startPresenceTimer();
  }
});

watch(
  () => router.currentRoute.value.fullPath,
  () => {
    if (!auth.isLoggedIn || isPublicRoute.value) {
      return;
    }

    markInteraction();
    void syncPresence();
  },
);

watch(
  () => auth.authenticated,
  (authenticated) => {
    if (!authenticated || isPublicRoute.value) {
      stopPresenceTimer();
      return;
    }

    markInteraction();
    void syncPresence();
    startPresenceTimer();
  },
);

onBeforeUnmount(() => {
  stopPresenceTimer();
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  window.removeEventListener("focus", markInteraction);
  window.removeEventListener("keydown", markInteraction);
  window.removeEventListener("pointerdown", markInteraction);
  window.removeEventListener("mousemove", markInteraction);
});
</script>

<template>
  <section
    v-if="!auth.initialized"
    class="flex min-h-screen items-center justify-center bg-gray-100 dark:bg-gray-900"
  >
    <div class="text-center text-sm text-gray-500 dark:text-gray-400">
      <img
        class="mx-auto mb-3 h-12 w-auto animate-pulse object-contain"
        src="/images/logo.png"
        alt="Medic 1"
      />
      Checking your session…
    </div>
  </section>
  <router-view v-else-if="isPublicRoute" />
  <div v-else-if="auth.isLoggedIn">
    <ConfettiAnimation />
    <LayoutDefault />
    <UndoToast />
  </div>
  <LoginView v-else />
</template>
